"use client";

import { useEffect, useState } from "react";
import VegaEmbed from "@/components/VegaEmbed";
import { findFilename, getFilter2Path, getMapItem, getRelatedItems } from "./Algorithm";
import { Button } from "./ui/button";
import * as XLSX from "xlsx";
import { getCategoryDomain, getPresentCategoryValues } from "../lib/category-order";
// import แบบ relative เท่านั้น — ishealth-platform ไม่มี alias "@/lib/*" ชี้เข้า submodule นี้
import {
  dimName,
  labelForDim,
  labelForFilter,
  labelForMetric,
  metricName,
  sortOptionsByLabel,
} from "../lib/labels";
import "./Chart.css";

/**
 * Chart Component - แสดงกราฟ Vega-Lite พร้อม dropdown สำหรับเลือก metric, dimension, group, filter
 *
 * Props:
 *   - filename:     ชื่อไฟล์ dataset (ใช้สร้างชื่อ parquet file)
 *   - filter:       ค่า filter เริ่มต้น
 *   - colorValues:  ค่าสีที่กำหนดไว้ล่วงหน้า (ถ้ามี)
 *   - duckLoaded:   boolean บอกว่า DuckDB พร้อมใช้งานแล้วหรือยัง
 *   - datasetId:    ID ของ dataset
 *   - prefix:       prefix path สำหรับ dataset (สำหรับ division)
 *   - width/height: ขนาด container
 *   - onEvent:      callback สำหรับเรียก MainInterface handler (เช่น GetChartData)
 *   - previewItems: รายการ chart ทั้งหมดใน dataset (ใช้สร้าง dropdown options)
 *   - showChart:    boolean ถ้า false จะแสดงปุ่ม "<<Preview" กลับไปหน้า preview
 *   - setState:     callback สำหรับ set state ของ parent component
 *
 * mapItem Structure (Array 5 ตัว):
 *   [0] = Metric/Agg     เช่น "user_id__distinct", "engagement_time_min__sum"
 *   [1] = Dimension       เช่น "campaign", "ym", "country"
 *   [2] = Group           เช่น "country", null (optional, ใช้เป็นสีใน stacked bar)
 *   [3] = Filter 1        เช่น "ym", "y" (optional, key ของ filter)
 *   [4] = Filter 2        เช่น "campaign", null (optional, key ของ filter ตัวที่ 2)
 *
 * Parquet Filename Pattern:
 *   {dim1}__{dim2}.{metric}.{filterKey1}__{filterKey2}.parquet
 *   ตัวอย่าง: campaign__country.user_id__distinct.ym__2024-09.parquet
 */
export default function Chart(props: any) {

  let { filename, filter, colorValues } = props;

  // --- State ---
  let [mode, setMode] = useState("Chart");            // "Chart" หรือ "Table" (Table ยังไม่ได้ใช้งาน)
  let [chartData, setChartData] = useState<any[]>([]); // ข้อมูลจาก DuckDB query สำหรับ Vega chart
  let [forExcel, setForExcel] = useState<any[]>([]);   // ข้อมูล array-of-arrays สำหรับ export Excel (pivot แล้ว)
  let [spec, setSpec] = useState<any>(null);           // spec.json ของ dataset (บอก dim_type, status ของแต่ละ dimension)
  let [dimList, setDimList] = useState<string[]>([]);  // dimensions ที่เลือก เช่น ["campaign"] หรือ ["ym", "country"]
  let [mapItem, setMapItem] = useState(getMapItem(filename, filter)); // dropdown selections ทั้ง 5 ตัว
  let [parquetAxis, setParquetAxis] = useState(false); // true = transpose chart (สลับแกน X/Y เป็น horizontal bar)
  // โหมดแสดงผลล้วน ๆ เหมือน parquetAxis/limit — ห้ามใส่ใน deps ของ effect ที่ query parquet
  let [dodgeRequested, setDodgeRequested] = useState(false); // true = ขอแท่งเรียงข้างกันแทนแท่งซ้อน
  let [limit, setLimit] = useState<string>("25");      // Limit for chart data items
  let [chartDataError, setChartDataError] = useState<string | null>(null);

  // --- Data Fetching ---
  // เมื่อ mapItem หรือ duckLoaded เปลี่ยน จะสร้างชื่อ parquet file แล้วเรียก GetChartData
  useEffect(() => {
    if (!props.duckLoaded) return console.log("No duck");

    let getChartData = async () => {
      // สร้าง dimList จาก mapItem[1] (dimension) และ mapItem[2] (group)
      let dimList_ = [mapItem[1]];
      if (mapItem[2]) dimList_.push(mapItem[2]);

      setDimList([...dimList_]);

      // ชื่อไฟล์ต้องมาจาก chartinfo เท่านั้น ห้ามประกอบเอง — pipeline ยุบ _ ซ้ำ ตัด _ หัวท้าย
      // และตัดชื่อที่ยาวเกิน 240 ไบต์แล้วต่อ md5 ซึ่ง UI เลียนแบบไม่ได้ (ดู findFilename)
      let fname = findFilename(mapItem, props.previewItems || []);

      // ล้างกราฟเดิมระหว่างรอ parquet ชุดใหม่ เพื่อไม่ให้ Vega
      // คำนวณช่วงของ metric จาก spec ชั่วคราวที่ยังไม่มีข้อมูล
      setChartData([]);
      setForExcel([]);
      setChartDataError(null);

      if (!fname) {
        console.warn("ไม่พบกราฟที่ตรงกับตัวเลือกนี้ใน chartinfo", mapItem);
        setChartDataError("ไม่พบกราฟที่ตรงกับตัวเลือกนี้");
        return;
      }

      // เรียก MainInterface.GetChartData เพื่อ fetch parquet, query DuckDB, แล้ว set data
      // pipeline ใส่กราฟที่ไม่มีข้อมูลลง chartinfo ด้วย (gen_thumbnails.py ต่อ finished_tasks
      // ก่อนเช็คว่า do_gen_thumbnail คืน None) ตัวเลือกพวกนั้นจึงไม่มีไฟล์ parquet จริง
      // ต้องจับไว้ ไม่งั้นเป็น unhandled rejection แล้วผู้ใช้เห็นกราฟว่างโดยไม่รู้สาเหตุ
      props.onEvent({
        message: "GetChartData", params: {
          filename: fname,
          datasetId: props.datasetId,
          dimList: dimList_,
          prefix: props.prefix || "",
          agg: mapItem[0],
          setData: setChartData,
          setForExcel: setForExcel,
          setSpec: setSpec,
        }
      })?.catch?.((error: any) => {
        console.warn("GetChartData failed:", fname, error);
        setChartDataError("ไม่มีข้อมูลสำหรับตัวเลือกนี้");
      });
    }
    getChartData();
  }, [mapItem, props.duckLoaded]);

  // --- Excel Download ---
  // ถ้า parquetAxis = true จะ transpose ข้อมูลก่อน (สลับแถว/คอลัมน์)
  const downloadExcel = () => {
    console.log("forExcel: ", forExcel);
    let data = forExcel;
    if (parquetAxis && data.length > 0) {
      // Transpose: สลับแถว <-> คอลัมน์
      data = data[0].map((_: any, colIndex: number) =>
        data.map((row: any) => row[colIndex])
      );
    }
    let workbook = XLSX.utils.book_new();
    workbook.SheetNames.push("Data");
    const sheet = XLSX.utils.aoa_to_sheet(data);
    workbook.Sheets["Data"] = sheet;
    XLSX.writeFile(workbook, filename + ".xlsx", { bookType: "xlsx", type: "buffer" });
  }

  // อัปเดต mapItem ที่ index ที่กำหนด (ใช้กับทุก dropdown)
  const setMapItemAtIndex = (value: string, index: number) => {
    let mapItem_ = mapItem.map((item: string, i: number) => i === index ? value : item);
    setMapItem(mapItem_);
  }

  // ตรวจสอบว่า dimension แรกเป็นเวลาหรือไม่ (ymd, ym, y หรือ dim_type === 'time')
  // ถ้าเป็นเวลา จะ sort ascending แทน descending
  let timeDim = () => {
    return ["ymd", "ym", "y"].includes(dimList[0]) || spec?.[dimList[0]]?.dim_type == 'time'
  }

  let agg = mapItem[0]; // Metric ที่เลือก เช่น "user_id__distinct"

  // --- Vega-Lite Spec ---

  // Calculate limit
  let limitVal = limit === "All" ? chartData.length : parseInt(limit);


  // Filter chartData based on limit
  let filteredChartData = [...chartData];
  if (limitVal < filteredChartData.length) {
    if (timeDim()) {
      // For time dimensions, we want the LATEST N time periods
      // 1. Get unique time values
      const uniqueTimes = Array.from(new Set(filteredChartData.map((d: any) => d[dimList[0]])));
      // 2. Sort descending (latest first)
      uniqueTimes.sort((a: any, b: any) => {
        if (a < b) return 1;
        if (a > b) return -1;
        return 0;
      });
      // 3. Take top N
      const topTimes = uniqueTimes.slice(0, limitVal);
      // 4. Filter data to include only these times
      filteredChartData = filteredChartData.filter((d: any) => topTimes.includes(d[dimList[0]]));
      // 5. Sort data by time ascending for display
      filteredChartData.sort((a: any, b: any) => {
        if (a[dimList[0]] < b[dimList[0]]) return -1;
        if (a[dimList[0]] > b[dimList[0]]) return 1;
        return 0;
      });
    } else {
      // For other dimensions, we want TOP N by total metric value
      // 1. Aggregate sums by dimension
      const dimSums: { [key: string]: number } = {};
      filteredChartData.forEach((d: any) => {
        const key = d[dimList[0]];
        dimSums[key] = (dimSums[key] || 0) + (d[agg] || 0);
      });
      // 2. Get unique dimensions and sort by sum descending
      const uniqueDims = Object.keys(dimSums);
      uniqueDims.sort((a, b) => dimSums[b] - dimSums[a]);
      // 3. Take top N
      const topDims = uniqueDims.slice(0, limitVal);
      // 4. Filter data to include only these dimensions
      filteredChartData = filteredChartData.filter((d: any) => topDims.includes(d[dimList[0]]));
      // 5. Sort data by metric descending (or by dimension if needed)
      filteredChartData.sort((a: any, b: any) => {
        return (b[agg] || 0) - (a[agg] || 0);
      });
    }
  }

  // คำนวณขนาดกราฟตามจำนวน bar
  // (ตัวคูณความกว้าง/สูงอยู่ล่างบล็อก dodge เพราะต้องรู้ก่อนว่าแท่งซ้อนหรือเรียงข้างกัน)
  const uniqueBars = new Set(filteredChartData.map((d: any) => d[dimList[0]])).size;

  // หัวแกน/หัว legend/tooltip ใช้ชื่อแบบไม่มี key ต่อท้าย เพราะพื้นที่จำกัด
  // (dropdown ใช้แบบมี key เพื่อ cross-check กับชื่อไฟล์ parquet ได้)
  const dimTitle = dimName(spec, dimList[0]);
  const metricTitle = metricName(spec, agg);
  const groupTitle = dimName(spec, dimList[1]);

  // Encoding สำหรับแกน ordinal (dimension) และ quantitative (metric)
  // ใช้สลับกันตาม parquetAxis (transpose)
  const ordinalEnc: any = {
    field: dimList[0],
    type: "ordinal",
    title: dimTitle,
    sort: timeDim() ? { field: dimList[0], order: "ascending" }
      : { op: "sum", field: agg, order: "descending" },
    ...(parquetAxis ? {} : { axis: { labelAngle: uniqueBars > 6 ? -45 : 0 } }),
  };
  const quantEnc: any = {
    field: agg,
    type: "quantitative",
    title: metricTitle,
  };
  const groupField = dimList[1];
  const groupScaleDomain = dimList.length >= 2
    ? getCategoryDomain(
      spec,
      groupField,
      filteredChartData.map((row: any) => row[groupField]),
      colorValues,
    )
    : [];
  const groupLegendValues = getPresentCategoryValues(
    filteredChartData.map((row: any) => row[groupField]),
    groupScaleDomain,
  );
  const groupOrderField = "__ischart_group_order";
  const groupPositions = new Map(
    groupScaleDomain.map((value, index) => [`${typeof value}:${String(value)}`, index]),
  );
  const chartValues = dimList.length >= 2
    ? filteredChartData.map((row: any) => ({
      ...row,
      [groupOrderField]: groupPositions.get(
        `${typeof row[groupField]}:${String(row[groupField] ?? "")}`,
      ) ?? groupScaleDomain.length,
    }))
    : filteredChartData;

  // --- แท่งซ้อน vs แท่งเรียงข้างกัน ---
  // นับจำนวนกลุ่มจาก groupLegendValues ไม่ใช่ groupScaleDomain — domain รวมค่าจาก
  // spec._category_orders ที่อาจไม่ได้อยู่บนกราฟรอบนี้ (userAgent ของ mehealth_scan_qr
  // มี 289 ค่าใน domain แต่บนจอมีไม่กี่ค่า) ส่วน band ย่อยของ offset ที่ Vega สร้างจริง
  // เอา domain จากข้อมูลเท่านั้น ตัวเลขที่ตรงกันจึงเป็นค่าที่ปรากฏบนกราฟ
  //
  // เกิน DODGE_MAX_GROUPS แล้วความกว้างของ band หารด้วยจำนวนกลุ่มเหลือระดับ sub-pixel
  // ปุ่มจึงเทาไว้แล้วถอยกลับไปซ้อนเอง ดีกว่าปล่อยให้ผู้ใช้เห็นแท่งเส้นบาง ๆ เป็นร้อยแท่ง
  const DODGE_MAX_GROUPS = 12;
  const isDiff = !!agg?.includes("__diff");
  const hasGroup = dimList.length >= 2;
  const dodgeGroupCount = hasGroup ? groupLegendValues.length : 0;
  const dodgeAvailable = hasGroup && !isDiff && dodgeGroupCount <= DODGE_MAX_GROUPS;
  // metric __diff เรียงข้างกันเสมอ — ค่าติดลบซ้อนกันแล้วความยาวแท่งไม่ตรงกับตัวเลขใด ๆ
  const dodged = hasGroup && (isDiff || (dodgeRequested && dodgeAvailable));

  // แท่งเรียงข้างกันต้องเผื่อที่ตามจำนวนกลุ่ม ไม่งั้นแท่งบางจนอ่านไม่ออก
  // (กติกาเดียวกับ MelabFavorites.buildChart) ตอน dodge ปล่อยเพดาน props.width แล้วให้
  // scroll แนวนอนแทน เพราะบีบ 25 แท่ง x 9 กลุ่ม ให้พอดีจอจะเหลือแท่งละไม่กี่ pixel
  const maxBarWidth = 150;
  const maxCanvasPx = 6000;   // เพดานกันกราฟยาวจนเบราว์เซอร์อืด — เกินนี้ให้ลด Limit เอา
  const bandWidth = dodged ? Math.max(maxBarWidth, dodgeGroupCount * 26) : maxBarWidth;
  const bandHeight = dodged ? Math.max(25, dodgeGroupCount * 22) : 25;
  const calculatedWidth = dodged
    ? Math.max(400, Math.min(uniqueBars * bandWidth, maxCanvasPx))
    : Math.max(400, Math.min(uniqueBars * bandWidth, props.width * 0.6));
  const calculatedHeight = dodged
    ? Math.max(400, Math.min(uniqueBars * bandHeight, maxCanvasPx))
    : Math.max(400, uniqueBars * bandHeight);
  // ตอน dodge ratio นี้วัดเทียบ band ย่อยของ offset ไม่ใช่ band ของ dimension
  // ใช้ 0.4 เหมือนตอนซ้อนแล้วแท่งจะเหลือ 40% ของช่องที่แคบอยู่แล้ว
  const barWidthRatio = dodged ? 0.9 : (uniqueBars <= 4 ? 0.4 : 0.8);
  // ป้ายตัวเลขบนแท่งแนวตั้งที่แคบกว่านี้ทับกันจนอ่านไม่ออก — หมุนตั้งแทน
  const barSlotPx = dodged ? bandWidth / Math.max(1, dodgeGroupCount) : bandWidth;
  const rotateLabel = dodged && !parquetAxis && barSlotPx < 34;

  // สร้าง Vega-Lite spec
  // - ปกติ: x = ordinal (dimension), y = quantitative (metric) → vertical bar
  // - transpose: x = quantitative, y = ordinal → horizontal bar
  let vegaSpec: any = {
    data: { values: chartValues },
    title: "",
    width: parquetAxis ? props.width * 0.5 : calculatedWidth,
    height: parquetAxis ? calculatedHeight : props.height * 0.5,
    encoding: {
      x: parquetAxis ? quantEnc : ordinalEnc,
      y: parquetAxis ? ordinalEnc : quantEnc,
    },
    layer: [
      // Layer 1: Bar chart
      {
        mark: parquetAxis
          ? { type: "bar", height: { band: barWidthRatio } }
          : { type: "bar", width: { band: barWidthRatio } },
        encoding: {
          // ถ้ามี dimension ที่ 2 (group) → ใช้เป็นสี + tooltip
          ...(dimList.length < 2 ? {}
            : {
              color: {
                field: dimList[1],
                title: groupTitle,
                ...(groupScaleDomain
                  ? {
                    type: "nominal",
                    sort: groupScaleDomain,
                    scale: { domain: groupScaleDomain },
                    legend: { values: groupLegendValues, title: groupTitle },
                  }
                  : {}),
              },
              tooltip: [
                { field: dimList[1], type: "nominal", title: groupTitle },
                { field: agg, type: "quantitative", title: metricTitle },
              ]
            }),
        },
      },
      // Layer 2: Text label แสดงผลรวมบน/ข้าง bar (ไม่แสดงถ้าเป็น diff)
      ...(agg?.includes('__diff') ? [] : [
        {
          mark: {
            type: "text",
            ...(parquetAxis
              ? { dx: 5, align: "left" }
              : rotateLabel
                ? { angle: -90, align: "left", baseline: "middle", dy: -4 }
                : { dy: -5 }),
            fontWeight: "bold",
            fontSize: 12,
            color: "black"
          },
          encoding: {
            text: { aggregate: "sum", field: agg, type: "quantitative" },
            // ต้องใส่ title ให้ตรงกับ quantEnc ไม่งั้น Vega รวม layer แล้วขึ้นหัวแกนซ้อนกัน
            // เป็น "id__count, Sum of id__count"
            ...(parquetAxis
              ? { x: { aggregate: "sum", field: agg, type: "quantitative", title: metricTitle } }
              : { y: { aggregate: "sum", field: agg, type: "quantitative", title: metricTitle } }),
          },
        }])
    ]
  }
  // แท่งเรียงข้างกัน = ใส่ offset ที่ฝั่งแกน ordinal ซึ่งสลับข้างตาม parquetAxis
  // ใส่ xOffset ตอน x เป็น quantitative (คือตอน transpose) แล้ว Vega-Lite ทิ้งทั้ง channel
  // เงียบ ๆ ("xOffset dropped because x is continuous") กราฟกลับไปซ้อนโดยไม่มีอะไรบอก
  //
  // order มีความหมายเฉพาะแท่งซ้อน — มันคือลำดับชั้นในแท่ง ตอนเรียงข้างกันไม่มีชั้นให้เรียง
  if (dodged) {
    vegaSpec.encoding[parquetAxis ? "yOffset" : "xOffset"] = {
      field: dimList[1],
      type: "nominal",
      sort: groupScaleDomain,
    };
  } else if (dimList.length >= 2) {
    vegaSpec.layer[0].encoding.order = {
      field: groupOrderField,
      type: "quantitative",
      sort: "ascending",
    };
  }

  // --- Dropdown Options ---
  // getRelatedItems คืน array 5 ชุด สำหรับแต่ละ dropdown
  // s[0] = metric options, s[1] = dim options, s[2] = group options, s[3] = filter1 options, s[4] = filter2 options
  let s = getRelatedItems(mapItem, props.previewItems);

  // ค่าใน s เป็น token ดิบจากชื่อไฟล์ parquet (เช่น "age_group", "id__count")
  // แปลงเป็นข้อความที่อ่านรู้เรื่องด้วย display_name ใน spec.json ที่โหลดมาแล้ว
  // spec เป็น null ตอน render รอบแรก (ถูก set ใน GetChartData) resolver จะคืน token เดิมไปก่อน
  const optionLabel: ((item: any) => string)[] = [
    (item: any) => labelForMetric(spec, item),  // [0] Metric
    (item: any) => labelForDim(spec, item),     // [1] Dimension
    (item: any) => labelForDim(spec, item),     // [2] Group
    (item: any) => labelForFilter(spec, item),  // [3] Filter
    (item: any) => labelForFilter(spec, item),  // [4] Filter 2
  ];
  // เรียงใหม่ตาม label ที่เห็นจริง — ลำดับเดิมจาก selectable() เรียงตาม token ดิบ
  const options = optionLabel.map(
    (toLabel, index) => sortOptionsByLabel(s[index] || [], toLabel));

  // --- คำอธิบายว่าทำไม Filter 2 เทา ---
  // pipeline สร้างกราฟที่มี Filter 2 ไว้เฉพาะบางรูปแบบ และ dropdown เดินได้ทีละ 1 ช่อง
  // กราฟที่ใช้ Filter 2 ได้จึงมักอยู่ห่างเกิน 1 ก้าว ผู้ใช้เห็นแค่ช่องเทาโดยไม่รู้สาเหตุ
  const filter2Disabled = options[4].length === 1 && options[4][0] === null;
  const filter2Path = filter2Disabled
    ? getFilter2Path(mapItem, props.previewItems || [])
    : null;
  const FIELD_NAMES = ["ตัวชี้วัด", "มิติ", "กลุ่ม", "ตัวกรอง"];
  // ค่า null คือ "ไม่เลือกอะไร" ซึ่งเขียนว่า "กลุ่ม = — ไม่ระบุ —" แล้วอ่านไม่รู้เรื่อง
  const CLEAR_NAMES: Record<number, string> = { 2: "เอาการแบ่งสีออก", 3: "เอาตัวกรองออก" };
  const filter2Steps = (filter2Path || []).map(({ index, value }) =>
    value === null || value === undefined
      ? (CLEAR_NAMES[index] || `เอา${FIELD_NAMES[index]}ออก`)
      : `${FIELD_NAMES[index]} = ${optionLabel[index](value)}`);
  const filter2Notice = !filter2Disabled ? null
    : filter2Path === null
      ? { short: "ชุดข้อมูลนี้ไม่มีตัวกรอง 2", detail: "ยังไม่มีกราฟที่ใช้ตัวกรองสองชั้นในชุดข้อมูลนี้" }
      : filter2Steps.length === 0
        ? null
        : {
          short: `ต้องเปลี่ยน ${filter2Steps[0]}`
            + (filter2Steps.length > 1 ? ` +${filter2Steps.length - 1}` : ""),
          detail: "ตัวกรอง 2 ใช้ได้เฉพาะบางรูปแบบกราฟ ต้องเปลี่ยน:\n"
            + filter2Steps.map((step) => `• ${step}`).join("\n"),
        };

  // --- คำอธิบายว่าทำไมปุ่มแท่งเรียงข้างกันกดไม่ได้ ---
  // เพดานจำนวนกลุ่มเป็นกฎที่ตัดตัวเลือกของผู้ใช้ทิ้ง จึงต้องบอกบนหน้าจอว่าตัดเพราะอะไร
  // และตัดที่เท่าไหร่ ไม่ใช่ปล่อยให้เห็นแค่ปุ่มเทา
  const dodgeHint = isDiff
    ? "ตัวชี้วัดส่วนต่างเรียงแท่งข้างกันเสมอ — ค่าติดลบซ้อนกันแล้วอ่านไม่ได้"
    : !hasGroup
      ? "เลือก “กลุ่ม (แบ่งสี)” ก่อน จึงจะเรียงแท่งข้างกันได้"
      : !dodgeAvailable
        ? `กลุ่มนี้มี ${dodgeGroupCount} ค่า เรียงข้างกันแล้วแท่งจะบางเกินอ่าน `
          + `(รองรับไม่เกิน ${DODGE_MAX_GROUPS} ค่า — ลด Limit หรือเปลี่ยนกลุ่มให้ค่าน้อยลง)`
        : dodged
          ? "กดเพื่อกลับไปเป็นแท่งซ้อน (ดูยอดรวมของแต่ละแท่ง)"
          : "กดเพื่อเรียงแท่งข้างกัน (เทียบกลุ่มกันเอง)";

  // --- Render ---
  return (
    <div id="Chart" className="ischart-chart">
      {/* ปุ่มกลับไปหน้า Preview (แสดงเมื่อเปิดจาก dialog) */}
      {!props.showChart &&
        <div
          style={{ backgroundColor: "lightblue", marginRight: "10px", cursor: "pointer" }}
          onClick={(e: any) => {
            props.setState({ dimList: [], filter: null, colorValues: [] });
          }}>
          &lt;&lt;Preview
        </div>}

      {/* Row 1: Metric dropdown + Transpose/Download buttons (align right) */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "12px", color: "gray", whiteSpace: "nowrap", width: "120px", textAlign: "right" }}>Metric / ตัวชี้วัด</span>
        <select className="mapItem" value={mapItem[0] || ""}
          onChange={(e: any) => {
            setMapItemAtIndex(e.target.value === "" ? null : e.target.value, 0)
          }}
        >
          {options[0].map((item: any, index: number) => (
            <option key={index} value={item || ""}>{optionLabel[0](item)}</option>
          ))}
        </select>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
          <Button
            variant={parquetAxis ? "success" : "secondary"}
            onClick={() => setParquetAxis(!parquetAxis)}>
            Transpose
          </Button>
          {/* title อยู่ที่ span ไม่ใช่ที่ปุ่ม เพราะเบราว์เซอร์ไม่แสดง tooltip ของ element
              ที่ disabled ซึ่งเป็นตอนที่ผู้ใช้ต้องการคำอธิบายมากที่สุด
              variant ดูจาก dodged (ผลจริง) ไม่ใช่ dodgeRequested เวลาระบบถอยกลับไปซ้อนเอง
              เพราะกลุ่มเยอะเกิน ปุ่มจะได้ไม่ค้างเขียวทั้งที่กราฟซ้อนอยู่ */}
          <span style={{ display: "inline-flex" }} title={dodgeHint}>
            <Button
              variant={dodged ? "success" : "secondary"}
              disabled={!dodgeAvailable}
              onClick={() => setDodgeRequested(!dodgeRequested)}>
              แท่งเรียงข้างกัน
            </Button>
          </span>
          <Button
            variant="info"
            onClick={() => downloadExcel()}>
            Download Excel
          </Button>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", borderLeft: "1px solid #ccc", paddingLeft: "8px" }}>
            <span style={{ fontSize: "12px", color: "gray", whiteSpace: "nowrap" }}>Limit:</span>
            <select
              className="mapItem"
              style={{ width: "80px" }}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="All">All</option>
            </select>
          </div>
        </div>
      </div>

      {/* Row 2: Dimension + Group dropdowns */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "12px", color: "gray", whiteSpace: "nowrap", width: "120px", textAlign: "right" }}>Dimension / มิติ</span>
        <select className="mapItem" value={mapItem[1] || ""}
          onChange={(e: any) => {
            setMapItemAtIndex(e.target.value === "" ? null : e.target.value, 1)
          }}>
          {options[1].map((item: any, index: number) => (
            <option key={index} value={item || ""}>{optionLabel[1](item)}</option>
          ))}
        </select>
        <span style={{ fontSize: "12px", color: "gray", whiteSpace: "nowrap", width: "120px", textAlign: "right" }}>Group / กลุ่ม (แบ่งสี)</span>
        <select className="mapItem" value={mapItem[2] || ""}
          disabled={options[2].length === 1 && options[2][0] === null}
          onChange={(e: any) => {
            setMapItemAtIndex(e.target.value === "" ? null : e.target.value, 2)
          }}>
          {options[2].map((item: any, index: number) => (
            <option key={index} value={item || ""}>{optionLabel[2](item)}</option>
          ))}
        </select>
      </div>

      {/* Row 3: Filter 1 + Filter 2 dropdowns */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "12px", color: "gray", whiteSpace: "nowrap", width: "120px", textAlign: "right" }}>Filter / ตัวกรอง</span>
        <select className="mapItem" value={mapItem[3] || ""}
          onChange={(e: any) => {
            setMapItemAtIndex(e.target.value === "" ? null : e.target.value, 3)
          }}>
          {options[3].map((item: any, index: number) => (
            <option key={index} value={item || ""}>{optionLabel[3](item)}</option>
          ))}
        </select>
        <span style={{ fontSize: "12px", color: "gray", whiteSpace: "nowrap", width: "120px", textAlign: "right" }}>Filter 2 / ตัวกรอง 2</span>
        <select className="mapItem" value={mapItem[4] || ""}
          disabled={options[4].length === 1 && options[4][0] === null}
          onChange={(e: any) => {
            setMapItemAtIndex(e.target.value === "" ? null : e.target.value, 4)
          }}>
          {options[4].map((item: any, index: number) => (
            <option key={index} value={item || ""}>{optionLabel[4](item)}</option>
          ))}
        </select>
        {/* title ต้องอยู่ที่ span ไม่ใช่ที่ select เพราะเบราว์เซอร์ส่วนใหญ่
            ไม่แสดง tooltip ของ element ที่ disabled */}
        {filter2Notice && (
          <span
            className="ischart-cube-status"
            role="status"
            aria-live="polite"
            title={filter2Notice.detail}
          >
            <span className="ischart-cube-status-icon" aria-hidden="true">i</span>
            {filter2Notice.short}
          </span>
        )}
      </div>

      {/* Chart / Table display */}
      {
        mode === "Chart" && dimList.length > 0 && chartData.length > 0 ?
          <div className="ischart-chart-canvas">
            <VegaEmbed
              data={vegaSpec}
            />
          </div>

          : mode === "Table" && dimList.length > 0 ?
            <div style={{ height: "800px", overflowY: "auto" }}>
              {chartData.map((item: any, index: number) => (
                <div
                  key={index}
                  style={{
                    display: "flex", width: "300px", fontFamily: "lucida console",
                  }}>
                  <div style={{ flex: 1 }}>
                    {item.created_ym}
                  </div>
                  <div style={{ flex: 1, textAlign: "right" }}>
                    {item.id__count}
                  </div>
                </div>
              ))}
            </div>

            : chartDataError
              ? <div
                role="status"
                style={{ color: "#6b7280", fontSize: "13px", padding: "48px 0", textAlign: "center" }}>
                {chartDataError}
              </div>
              : <></>
      }
    </div >
  )
}
